import { useAccount } from "#contexts/AccountContext"
import { useCanPerformMutations } from "#hooks/use-can-perform-mutations"
import { useCreateCategory } from "#hooks/use-categories"
import { useI18n } from "#hooks/use-i18n"
import type { ParsedCSVRow } from "#lib/csv-parser"
import { parseDate, parseNumber } from "#lib/csv-parser"
import type { ImportConfig, ImportMapping } from "#lib/indexeddb-import-store"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import { format } from "date-fns"
import * as React from "react"
import { Dialog, DialogActions } from "../Dialog"
import { Button } from "../ui/button"
import { CategoryMatchingStep } from "./CategoryMatchingStep"
import { ColumnMappingStep } from "./ColumnMappingStep"
import { CsvConfigurationStep } from "./CsvConfigurationStep"
import { RowEditDialog } from "./RowEditDialog"
import { RowReviewStep, type RowPreview } from "./RowReviewStep"

interface ColumnMappingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  headers: string[]
  allRows: ParsedCSVRow[]
  categories: ExpenseCategory[]
  file: File | null
  onReParse: (file: File, config: ImportConfig) => Promise<void>
  onConfirm: (mapping: ImportMapping, config: ImportConfig, selectedRows: boolean[]) => void
  restoreToStep?: 1 | 2 | 3
}

export function ColumnMappingDialog({
  open,
  onOpenChange,
  headers,
  allRows,
  categories: initialCategories,
  file,
  onReParse,
  onConfirm,
  restoreToStep,
}: ColumnMappingDialogProps) {
  const { t } = useI18n()
  const { currentAccount } = useAccount()
  const canPerformMutations = useCanPerformMutations()
  const createCategory = useCreateCategory()
  const [categories, setCategories] = React.useState<ExpenseCategory[]>(initialCategories)
  const [isCreatingCategories, setIsCreatingCategories] = React.useState(false)

  React.useEffect(() => {
    setCategories(initialCategories)
  }, [initialCategories])

  const [mapping, setMapping] = React.useState<ImportMapping>({})
  const [config, setConfig] = React.useState<ImportConfig>({
    separator: ";",
    dateFormat: "dd/MM/yyyy",
    decimalSeparator: ",",
  })
  const [selectedRows, setSelectedRows] = React.useState<boolean[]>(
    new Array(allRows.length).fill(true)
  )
  const [categoryMappings, setCategoryMappings] = React.useState<Map<string, number | "new">>(
    new Map()
  )
  const categoryMappingsRef = React.useRef(categoryMappings)
  React.useEffect(() => {
    categoryMappingsRef.current = categoryMappings
  }, [categoryMappings])
  const [allCsvCategories, setAllCsvCategories] = React.useState<Set<string>>(new Set())
  const [editingRowIndex, setEditingRowIndex] = React.useState<number | null>(null)
  const [editedRows, setEditedRows] = React.useState<Map<number, Partial<ParsedCSVRow>>>(new Map())
  const [currentStep, setCurrentStep] = React.useState<1 | 2 | 3>(1)
  const [rowViewFilter, setRowViewFilter] = React.useState<"all" | "needsAction">("needsAction")

  React.useEffect(() => {
    setSelectedRows(new Array(allRows.length).fill(true))
  }, [allRows.length])

  React.useEffect(() => {
    if (open) {
      if (restoreToStep) {
        setCurrentStep(restoreToStep)
      } else {
        setCurrentStep(1)
      }
    }
  }, [open, restoreToStep])

  React.useEffect(() => {
    if (!mapping.category || categories.length === 0) {
      return
    }

    const categoryColumn = Array.isArray(mapping.category) ? mapping.category[0] : mapping.category

    if (!categoryColumn) return

    const uniqueCategories = new Set<string>()
    const newMappings = new Map<string, number | "new">()

    for (const row of allRows) {
      const categoryName = row[categoryColumn]?.trim()
      if (categoryName) {
        uniqueCategories.add(categoryName)
      }
    }

    setAllCsvCategories(uniqueCategories)

    for (const categoryName of uniqueCategories) {
      const existingMapping = categoryMappings.get(categoryName)
      if (existingMapping !== undefined) {
        newMappings.set(categoryName, existingMapping)
        continue
      }

      const matched = categories.find(
        (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
      )
      if (matched) {
        newMappings.set(categoryName, matched.id)
      } else {
        newMappings.set(categoryName, "new")
      }
    }

    setCategoryMappings(newMappings)
  }, [mapping.category, categories, allRows])

  const handleMappingChange = (field: keyof ImportMapping, value: string | string[]) => {
    const NONE_VALUE = "__none__"
    const normalizedValue =
      typeof value === "string" && value === NONE_VALUE
        ? ""
        : Array.isArray(value)
          ? value.map((v) => (v === NONE_VALUE ? "" : v))
          : value
    setMapping((prev) => ({ ...prev, [field]: normalizedValue }))
  }

  const handleCategoryMappingChange = (categoryName: string, categoryId: number | "new") => {
    setCategoryMappings((prev) => {
      const newMap = new Map(prev)
      newMap.set(categoryName, categoryId)
      categoryMappingsRef.current = newMap
      return newMap
    })
  }

  const handleSeparatorChange = async (value: string) => {
    const newConfig = { ...config, separator: value }
    setConfig(newConfig)
    if (file) {
      try {
        await onReParse(file, newConfig)
        setMapping({})
        setSelectedRows(new Array(allRows.length).fill(true))
      } catch (error) {
        console.error("Failed to re-parse CSV:", error)
      }
    }
  }

  const handleDateFormatChange = (value: string) => {
    setConfig((prev) => ({ ...prev, dateFormat: value }))
  }

  const handleDecimalSeparatorChange = (value: "," | ".") => {
    setConfig((prev) => ({ ...prev, decimalSeparator: value }))
  }

  const handleSelectAll = (filteredIndices: number[]) => {
    setSelectedRows((prev) => {
      const newSelection = [...prev]
      filteredIndices.forEach((index) => {
        newSelection[index] = true
      })
      return newSelection
    })
  }

  const handleDeselectAll = (filteredIndices: number[]) => {
    setSelectedRows((prev) => {
      const newSelection = [...prev]
      filteredIndices.forEach((index) => {
        newSelection[index] = false
      })
      return newSelection
    })
  }

  const handleRowToggle = (index: number) => {
    setSelectedRows((prev) => {
      const newSelection = [...prev]
      newSelection[index] = !newSelection[index]
      return newSelection
    })
  }

  const handleConfirm = () => {
    const finalMapping: ImportMapping = {
      ...mapping,
      categoryMappings: Object.fromEntries(categoryMappings),
    }
    onConfirm(finalMapping, config, selectedRows)
  }

  const previewRow = (row: ParsedCSVRow, index: number): RowPreview | null => {
    if (!mapping.name || !mapping.amount || !mapping.date) {
      return null
    }

    const editedData = editedRows.get(index) || {}
    const mergedRow = { ...row, ...editedData }

    const nameColumns = Array.isArray(mapping.name) ? mapping.name : [mapping.name]
    const name = nameColumns
      .map((col) => mergedRow[col] || "")
      .filter(Boolean)
      .join(mapping.nameSeparator || " ")

    const descriptionColumns = Array.isArray(mapping.description)
      ? mapping.description
      : mapping.description
        ? [mapping.description]
        : []
    const description = descriptionColumns
      .map((col) => mergedRow[col] || "")
      .filter(Boolean)
      .join(mapping.descriptionSeparator || " ")

    const amountStr = mergedRow[mapping.amount] || ""
    const parsedAmount = parseNumber(amountStr, config.decimalSeparator)
    const amount = parsedAmount ? Math.abs(parsedAmount) : null

    const dateColumn = Array.isArray(mapping.date) ? mapping.date[0] : mapping.date
    const dateStr = mergedRow[dateColumn] || ""
    const date = parseDate(dateStr, config.dateFormat)

    const categoryColumn = Array.isArray(mapping.category) ? mapping.category[0] : mapping.category
    const categoryName = categoryColumn ? mergedRow[categoryColumn]?.trim() : undefined

    let categoryId: number | "new" | undefined = undefined
    if (categoryName) {
      let mappingId = categoryMappingsRef.current.get(categoryName)

      if (mappingId === undefined) {
        for (const [key, value] of categoryMappingsRef.current.entries()) {
          if (key.toLowerCase() === categoryName.toLowerCase()) {
            mappingId = value
            break
          }
        }
      }

      if (mappingId !== undefined) {
        categoryId = mappingId
      } else {
        const existingCategory = categories.find(
          (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
        )
        if (existingCategory) {
          categoryId = existingCategory.id
        }
      }
    }

    const missingFields: string[] = []
    if (!name) missingFields.push("Name")
    if (!amount || amount <= 0) missingFields.push("Amount")
    if (!date) missingFields.push("Date")
    if (!categoryId || (typeof categoryId === "string" && categoryId === "new")) {
      missingFields.push("Category")
    }

    return {
      name,
      description: description || undefined,
      amount: amount || 0,
      date: date ? format(date, "yyyy-MM-dd") : undefined,
      categoryId,
      categoryName,
      isValid: missingFields.length === 0,
      missingFields,
      rawRow: mergedRow as ParsedCSVRow,
    }
  }

  const canConfirm =
    mapping.name && mapping.amount && mapping.date && selectedRows.some((selected) => selected)

  const canProceedFromStep1 = true
  const canProceedFromStep2 = !!(mapping.name && mapping.amount && mapping.date)

  const handleNext = async () => {
    if (currentStep === 1 && canProceedFromStep1) {
      setCurrentStep(2)
    } else if (currentStep === 2 && canProceedFromStep2) {
      // Check if there are categories marked as "new" that need to be created
      const categoriesToCreate: string[] = []
      for (const [categoryName, categoryId] of categoryMappings.entries()) {
        if (categoryId === "new") {
          categoriesToCreate.push(categoryName)
        }
      }

      if (categoriesToCreate.length > 0 && currentAccount?.id) {
        // Create categories automatically before proceeding to step 3
        setIsCreatingCategories(true)
        try {
          const updatedMappings = new Map(categoryMappings)
          // Merge current categories with initial categories to ensure we have the latest
          const mergedCategories = [
            ...categories,
            ...initialCategories.filter((cat) => !categories.some((c) => c.id === cat.id)),
          ]
          const updatedCategories = [...mergedCategories]

          for (const categoryName of categoriesToCreate) {
            try {
              // Check if category already exists (case-insensitive)
              const existingCategory = updatedCategories.find(
                (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
              )

              if (existingCategory) {
                // Use existing category
                updatedMappings.set(categoryName, existingCategory.id)
              } else {
                // Create new category
                const newCategory = await createCategory.mutateAsync({
                  name: categoryName,
                  description: "",
                })
                updatedCategories.push(newCategory)
                updatedMappings.set(categoryName, newCategory.id)
              }
            } catch (error: any) {
              // If creation fails due to duplicate, try to find the existing category
              const errorMessage = error?.message || ""
              if (
                errorMessage.includes("already exists") ||
                errorMessage.includes("duplicate") ||
                errorMessage.includes("Conflict")
              ) {
                // Category might have been created by another process, check again
                const foundCategory = updatedCategories.find(
                  (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
                )
                if (foundCategory) {
                  updatedMappings.set(categoryName, foundCategory.id)
                } else {
                  console.error(`Category "${categoryName}" already exists but could not be found`)
                }
              } else {
                console.error(`Failed to create category "${categoryName}":`, error)
              }
              // Continue with other categories even if one fails
            }
          }

          // Update state with new categories and mappings
          setCategories(updatedCategories)
          setCategoryMappings(updatedMappings)
          categoryMappingsRef.current = updatedMappings
        } catch (error) {
          console.error("Failed to create categories:", error)
        } finally {
          setIsCreatingCategories(false)
        }
      }

      setCurrentStep(3)
    }
  }

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
    } else if (currentStep === 3) {
      setCurrentStep(2)
    }
  }

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t("components.expenseImport.csvConfiguration")
      case 2:
        return t("components.expenseImport.columnMapping")
      case 3:
        return t("components.expenseImport.review")
      default:
        return t("components.expenseImport.mapColumns")
    }
  }

  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return t("components.expenseImport.configureDescription")
      case 2:
        return t("components.expenseImport.mappingDescription")
      case 3:
        return t("components.expenseImport.reviewDescription")
      default:
        return ""
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={getStepTitle()}
      description={getStepDescription()}
      size="xl"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex items-center flex-shrink-0">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full border-2 flex-shrink-0 ${
                    currentStep === step
                      ? "border-primary bg-primary text-primary-foreground"
                      : currentStep > step
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-muted text-muted-foreground"
                  }`}
                >
                  {step}
                </div>
                <span
                  className={`ml-2 text-sm hidden sm:inline ${
                    currentStep === step ? "font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {step === 1
                    ? t("components.expenseImport.configuration")
                    : step === 2
                      ? t("components.expenseImport.mapping")
                      : t("components.expenseImport.review")}
                </span>
              </div>
              {step < 3 && (
                <div
                  className={`h-0.5 flex-1 mx-2 sm:mx-4 min-w-0 ${
                    currentStep > step ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="space-y-6 pb-6">
        {currentStep === 1 && (
          <CsvConfigurationStep
            config={config}
            onSeparatorChange={handleSeparatorChange}
            onDateFormatChange={handleDateFormatChange}
            onDecimalSeparatorChange={handleDecimalSeparatorChange}
          />
        )}

        {currentStep === 2 && (
          <>
            <ColumnMappingStep
              headers={headers}
              mapping={mapping}
              onMappingChange={handleMappingChange}
            />
            <CategoryMatchingStep
              allCsvCategories={allCsvCategories}
              categories={categories}
              categoryMappings={categoryMappings}
              onCategoryMappingChange={handleCategoryMappingChange}
            />
          </>
        )}

        {currentStep === 3 && (
          <RowReviewStep
            allRows={allRows}
            selectedRows={selectedRows}
            rowViewFilter={rowViewFilter}
            onRowViewFilterChange={setRowViewFilter}
            onRowToggle={handleRowToggle}
            onEditRow={setEditingRowIndex}
            previewRow={previewRow}
          />
        )}
      </div>

      {currentStep === 3 ? (
        (() => {
          const filteredRows = allRows
            .map((row, index) => {
              const preview = previewRow(row, index)
              const hasMissingFields = preview?.missingFields && preview.missingFields.length > 0
              return { row, index, preview, hasMissingFields }
            })
            .filter(({ hasMissingFields }) => {
              if (rowViewFilter === "needsAction") {
                return hasMissingFields
              }
              return true
            })

          const filteredIndices = filteredRows.map(({ index }) => index)
          const allFilteredSelected =
            filteredIndices.length > 0 && filteredIndices.every((index) => selectedRows[index])
          const filterLabel =
            rowViewFilter === "needsAction"
              ? t("components.expenseImport.needsAction")
              : t("components.expenseImport.all")

          return (
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  allFilteredSelected
                    ? handleDeselectAll(filteredIndices)
                    : handleSelectAll(filteredIndices)
                }
              >
                {allFilteredSelected
                  ? `${t("components.expenses.filters.deselectAll")} (${filterLabel})`
                  : `${t("components.expenses.filters.selectAll")} (${filterLabel})`}
              </Button>
              <DialogActions
                onCancel={handleBack}
                onConfirm={canConfirm && canPerformMutations ? handleConfirm : undefined}
                confirmText={t("components.expenseImport.startImport")}
                cancelText={t("components.expenseImport.back")}
                confirmVariant="default"
                isConfirmDisabled={!canPerformMutations || !canConfirm}
                confirmTooltip={
                  !canPerformMutations
                    ? t("components.expenses.tooltips.actionDisabled")
                    : undefined
                }
              />
            </div>
          )
        })()
      ) : (
        <DialogActions
          onCancel={
            isCreatingCategories
              ? undefined
              : currentStep === 1
                ? () => onOpenChange(false)
                : handleBack
          }
          onConfirm={
            isCreatingCategories
              ? undefined
              : (currentStep === 1 ? canProceedFromStep1 : canProceedFromStep2)
                ? handleNext
                : undefined
          }
          confirmText={
            isCreatingCategories
              ? t("components.expenseImport.creatingCategories")
              : t("components.expenseImport.next")
          }
          cancelText={
            currentStep === 1
              ? t("components.expenseImport.cancel")
              : t("components.expenseImport.back")
          }
          confirmVariant="default"
        />
      )}

      {editingRowIndex !== null && (
        <RowEditDialog
          open={editingRowIndex !== null}
          onOpenChange={(open) => !open && setEditingRowIndex(null)}
          row={allRows[editingRowIndex]}
          rowIndex={editingRowIndex}
          editedData={editedRows.get(editingRowIndex) || {}}
          mapping={mapping}
          config={config}
          categories={categories}
          categoryMappings={categoryMappings}
          onCategoryMappingChange={(categoryName, categoryId) => {
            handleCategoryMappingChange(categoryName, categoryId)
          }}
          onCategoriesChange={(newCategories) => {
            setCategories(newCategories)
          }}
          onSave={(data) => {
            const categoryColumn = Array.isArray(mapping.category)
              ? mapping.category[0]
              : mapping.category || ""
            const savedCategoryName = data[categoryColumn]?.trim()

            if (savedCategoryName) {
              const cat = categories.find(
                (c) => c.name.toLowerCase() === savedCategoryName.toLowerCase()
              )
              if (cat) {
                const newMap = new Map(categoryMappingsRef.current)
                newMap.set(savedCategoryName, cat.id)
                categoryMappingsRef.current = newMap
                setCategoryMappings(newMap)
              } else {
                console.warn(`Category "${savedCategoryName}" not found in categories list`)
              }
            }

            setEditedRows((prev) => {
              const newMap = new Map(prev)
              newMap.set(editingRowIndex, data)
              return newMap
            })

            setEditingRowIndex(null)
          }}
        />
      )}
    </Dialog>
  )
}
